import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const ForVenues = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [coastersAnimated, setCoastersAnimated] = useState(false);

  const handleGetCoasters = () => {
    navigate('/venue/apply');
  };

  // Scroll reveal and coaster animation
  useEffect(() => {
    // Trigger coaster animation on load
    const timer = setTimeout(() => {
      setCoastersAnimated(true);
    }, 200);

    const handleScroll = () => {
      document.querySelectorAll('.reveal').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add('active');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="bg-[#FEF7ED] text-[#1e1b4b]">
      <SEO 
        title="Free QR coasters for your venue — your guests discover local deals, you get free custom coasters"
        description="Free QR coasters for your venue — your guests discover local deals, you get free custom coasters"
        url="/for-venues"
        keywords="venue partnership, bar partnership, restaurant partnership, coaster advertising, passive income, venue revenue"
      />
      <style>{`
        .font-display { font-family: 'Faustina', Georgia, serif; }
        .font-body { font-family: 'DM Sans', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }
        
        .gradient-text {
          background: linear-gradient(135deg, #ea580c 0%, #f97316 50%, #ea580c 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        @keyframes pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(249, 115, 22, 0.3)); }
          50% { filter: drop-shadow(0 0 35px rgba(249, 115, 22, 0.5)); }
        }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        
        @keyframes coaster-in-1 {
          0% { opacity: 0; transform: rotate(-20deg) translateY(60px) scale(0.8); }
          100% { opacity: 1; transform: rotate(-20deg) translateY(0) scale(1); }
        }
        @keyframes coaster-in-2 {
          0% { opacity: 0; transform: rotate(8deg) translateY(80px) scale(0.8); }
          100% { opacity: 1; transform: rotate(8deg) translateY(0) scale(1); }
        }
        @keyframes coaster-in-3 {
          0% { opacity: 0; transform: rotate(-5deg) translateY(100px) scale(0.8); }
          100% { opacity: 1; transform: rotate(-5deg) translateY(0) scale(1); }
        }
        
        @keyframes float-1 {
          0%, 100% { transform: rotate(-20deg) translateY(0); }
          50% { transform: rotate(-20deg) translateY(-12px); }
        }
        @keyframes float-2 {
          0%, 100% { transform: rotate(8deg) translateY(0); }
          50% { transform: rotate(8deg) translateY(-14px); }
        }
        @keyframes float-3 {
          0%, 100% { transform: rotate(-5deg) translateY(0); }
          50% { transform: rotate(-5deg) translateY(-10px); }
        }
        
        @keyframes badge-in {
          0% { opacity: 0; transform: scale(0.5); }
          50% { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        
        .coaster-1.animate { animation: coaster-in-1 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards; opacity: 0; }
        .coaster-2.animate { animation: coaster-in-2 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards; opacity: 0; }
        .coaster-3.animate { animation: coaster-in-3 1s cubic-bezier(0.16, 1, 0.3, 1) 0.6s forwards; opacity: 0; }
        .badge-animate { animation: badge-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) 1.2s forwards; opacity: 0; }
        
        .coaster-float-1 { animation: float-1 4s ease-in-out infinite; }
        .coaster-float-2 { animation: float-2 4.5s ease-in-out infinite; }
        .coaster-float-3 { animation: float-3 5s ease-in-out infinite; }
        
        .hover-lift {
          transition: transform 0.4s ease, box-shadow 0.4s ease;
        }
        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 60px rgba(234, 88, 12, 0.15);
        }
        
        .reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.active {
          opacity: 1;
          transform: translateY(0);
        }
        
        details summary::-webkit-details-marker { display: none; }
        details summary { list-style: none; }
      `}</style>

      {/* HERO: The Free Coaster Offer */}
      <section className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30">
            <img src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1920" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#FEF7ED] via-[#FEF7ED]/95 to-[#FEF7ED]"></div>
          <div className="absolute top-20 left-10 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-200/40 rounded-full blur-3xl"></div>
        </div>
        
        {/* V5 Gradient Line at Bottom */}
        <div className="absolute bottom-0 left-0 w-full h-1 z-20" style={{ background: 'linear-gradient(to right, #8b5cf6 0%, #f97316 100%)' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* V5 Code Accent */}
              <p className="font-mono text-xs text-[#1e1b4b]/30">// for_venues</p>
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full border border-green-200">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-semibold text-green-700 font-body">100% Free for Venues</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1e1b4b] leading-tight" style={{ fontWeight: 500 }}>
                Get <span className="gradient-text">5,000 premium coasters</span> delivered free
              </h1>
              
              <p className="text-xl text-[#1e1b4b]/60 leading-relaxed font-body">
                Become a Tappd Hub Location. We send you beautiful, custom-designed coasters 
                at zero cost. Your customers discover local deals. Everyone wins.
              </p>
              
              {/* Key benefits */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  '5,000 free coasters',
                  'Your logo included',
                  'Free reorders forever',
                  'No strings attached'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Check className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="text-[#1e1b4b]/70 text-sm font-medium font-body">{benefit}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleGetCoasters}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full text-lg font-semibold text-white shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
                >
                  Apply for Free Coasters
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a 
                  href="#how-it-works" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#1e1b4b]/20 rounded-full text-lg font-semibold text-[#1e1b4b] hover:bg-white/50 transition-all"
                >
                  Learn More
                </a>
              </div>
              
              <p className="text-sm text-[#1e1b4b]/50 font-body">No credit card required • Takes 2 minutes to apply</p>
            </motion.div>
            
            {/* Visual: Fanned coaster images */}
            <motion.div 
              className="relative flex justify-center lg:justify-end"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] md:w-[440px] md:h-[440px] lg:w-[500px] lg:h-[500px]">
                {/* Back coaster */}
                <div className={`absolute top-12 -left-4 w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 ${coastersAnimated ? 'coaster-1 animate' : 'opacity-0'}`}>
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8c-community.png" alt="Tappd Coaster Design" className="w-full h-full object-contain drop-shadow-2xl"/>
                </div>

                {/* Front coaster */}
                <div className={`absolute -top-4 right-0 w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 pulse-glow ${coastersAnimated ? 'coaster-2 animate' : 'opacity-0'}`}>
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-3b-context.png" alt="Tappd Coaster Design" className="w-full h-full object-contain drop-shadow-2xl"/>
                </div>

                {/* Bottom coaster */}
                <div className={`absolute bottom-0 left-8 w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 ${coastersAnimated ? 'coaster-3 animate' : 'opacity-0'}`}>
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8a-community.png" alt="Tappd Coaster Design" className="w-full h-full object-contain drop-shadow-xl"/>
                </div>
                
                {/* 5,000 badge */}
                <div className={`absolute -top-2 right-8 md:right-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl px-5 py-3 shadow-xl z-10 ${coastersAnimated ? 'badge-animate' : 'opacity-0'}`}>
                  <div className="text-white font-bold text-3xl md:text-4xl font-display">5,000</div>
                  <div className="text-white/80 text-sm font-body">FREE coasters</div>
                </div>
                
                {/* Custom branded badge */}
                <div className={`absolute -bottom-2 left-0 bg-white rounded-xl px-5 py-4 shadow-xl border border-orange-100 z-10 ${coastersAnimated ? 'badge-animate' : 'opacity-0'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-base font-semibold text-[#1e1b4b] font-body">Custom Branded</div>
                      <div className="text-sm text-[#1e1b4b]/50 font-body">to Your Local Eatery</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT'S IN IT FOR YOU */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Why venues love being a <span className="gradient-text">Hub Location</span>
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 max-w-2xl mx-auto font-body">
              It's not just free coasters. It's a better experience for your customers.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 reveal">
            {[
              {
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                ),
                title: 'Save on coaster costs',
                description: "Premium quality coasters delivered free. No more ordering from suppliers. Running low? Email us and we'll ship more."
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                ),
                title: 'Engaged customers',
                description: "Give your customers something fun to do between drinks. They scan, discover local deals, and have a better time at your venue."
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                  </svg>
                ),
                title: 'Support local businesses',
                description: "Your venue becomes a hub for local discovery. Customers find great nearby businesses and you help strengthen the local economy."
              }
            ].map((benefit, idx) => (
              <div key={idx} className="hover-lift bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 border border-orange-100">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mb-6">
                  {benefit.icon}
                </div>
                <h3 className="font-display text-xl text-[#1e1b4b] mb-3">{benefit.title}</h3>
                <p className="text-[#1e1b4b]/60 font-body">{benefit.description}</p>
              </div>
            ))}
          </div>
          
          {/* No competition callout */}
          <div className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 reveal">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-green-800 mb-1 font-body">No competing food & beverage businesses</h4>
                <p className="text-green-700/70 text-sm font-body">
                  We never feature restaurants, bars, or cafes on our platform. Your customers discover complementary 
                  local businesses — salons, auto shops, fitness studios — not your competitors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-[#FEF7ED]" id="how-it-works">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              How to get your free coasters
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">Get started in 4 simple steps</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 reveal">
            {[
              {
                num: '1',
                icon: (
                  <svg className="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                ),
                title: 'Apply',
                desc: 'Fill out a simple application form'
              },
              {
                num: '2',
                icon: (
                  <svg className="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                ),
                title: 'Get Approved',
                desc: 'We review and approve within 48 hours'
              },
              {
                num: '3',
                icon: (
                  <svg className="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>
                  </svg>
                ),
                title: 'Choose Design',
                desc: 'Pick your coaster design & upload logo'
              },
              {
                num: '4',
                icon: (
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                  </svg>
                ),
                title: 'Receive Coasters',
                desc: '5,000 coasters delivered to your door',
                isGreen: true
              }
            ].map((step, idx) => (
              <div key={idx} className="hover-lift bg-white rounded-2xl p-6 border border-orange-100 text-center relative">
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 ${step.isGreen ? 'bg-gradient-to-br from-green-500 to-emerald-500' : 'bg-gradient-to-br from-orange-500 to-amber-500'} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`}>{step.num}</div>
                <div className={`w-14 h-14 ${step.isGreen ? 'bg-green-100' : 'bg-orange-100'} rounded-2xl flex items-center justify-center mx-auto mb-4 mt-2`}>
                  {step.icon}
                </div>
                <h3 className="font-display text-lg text-[#1e1b4b] mb-2">{step.title}</h3>
                <p className="text-sm text-[#1e1b4b]/60 font-body">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERFECT FOR YOUR VENUE */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Perfect for your venue
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">We work with all types of hospitality venues</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 reveal">
            {[
              { emoji: '🍺', title: 'Bars & Pubs', desc: 'Craft breweries, sports bars, cocktail lounges, dive bars, wine bars' },
              { emoji: '🍽️', title: 'Restaurants & Cafes', desc: 'Diners, coffee shops, fast casual, fine dining, bistros' },
              { emoji: '🎯', title: 'Entertainment Venues', desc: 'Music venues, bowling alleys, arcades, comedy clubs, pool halls' }
            ].map((type, idx) => (
              <div key={idx} className="hover-lift bg-white rounded-2xl p-8 border border-orange-100 shadow-sm text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">{type.emoji}</span>
                </div>
                <h3 className="font-display text-xl text-[#1e1b4b] mb-3">{type.title}</h3>
                <p className="text-[#1e1b4b]/60 text-sm font-body">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gradient-to-b from-[#FEF7ED] to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              What venue partners say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 reveal">
            {[
              {
                quote: "Free coasters that look way better than what we were ordering before? It was a no-brainer. Our customers love scanning them and discovering local deals.",
                name: 'Dave M.',
                business: "Ott's Tavern, Medford",
                emoji: '🍺'
              },
              {
                quote: "I was worried it would be some sketchy advertising thing, but it's actually super tasteful. The coasters look great and it helps local businesses. Win-win.",
                name: 'Maria K.',
                business: 'The Local Kitchen, Marlton',
                emoji: '🍽️'
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg border border-orange-100">
                <div className="flex items-center gap-1 text-orange-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                  ))}
                </div>
                <p className="text-[#1e1b4b]/70 mb-6 leading-relaxed font-body">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-xl">{testimonial.emoji}</div>
                  <div>
                    <div className="font-semibold text-[#1e1b4b] font-body">{testimonial.name}</div>
                    <div className="text-sm text-[#1e1b4b]/50 font-body">{testimonial.business}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Common questions
            </h2>
          </div>
          
          <div className="space-y-4 reveal">
            {[
              { q: 'Is this really free?', a: "Yes, 100% free. We never charge venues anything. We make money from local businesses who advertise on our platform — not from you. You get premium coasters delivered free, and free reorders whenever you need more." },
              { q: 'What kind of businesses will my customers see?', a: "Only non-competing local businesses: salons, auto shops, fitness studios, retail stores, home services, etc. We never feature restaurants, bars, or cafes — so you'll never be promoting a competitor." },
              { q: 'Can I customize the coaster design?', a: "Yes! You choose from our seasonal designs and we include your venue's logo on the back. The coasters are high-quality and look great on any table." },
              { q: 'What if I run out of coasters?', a: "Just let us know and we'll send more — still free. We want your venue to always have fresh Tappd coasters available for your customers." },
              { q: 'Is there a catch?', a: 'No catch. The only "requirement" is that you actually use the coasters in your venue. We make money when customers scan and discover local businesses — so the more your customers use them, the better for everyone.' }
            ].map((faq, idx) => (
              <details key={idx} className="group bg-[#FEF7ED] rounded-2xl border border-orange-100 overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer">
                  <span className="font-semibold text-lg text-[#1e1b4b] font-body">{faq.q}</span>
                  <ChevronDown className="w-5 h-5 text-orange-500 transition-transform group-open:rotate-180" />
                </summary>
                <div className="px-6 pb-6 text-[#1e1b4b]/70 font-body">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] text-white relative overflow-hidden" id="apply">
        {/* Background */}
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1920" alt="" className="w-full h-full object-cover opacity-20"/>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl mb-6">
            Ready to get your <span className="text-orange-400">free coasters?</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto mb-10 font-body">
            Join 50+ venues already partnered with Tappd. Application takes 2 minutes.
          </p>
          
          <button
            onClick={handleGetCoasters}
            className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full text-xl font-semibold shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
          >
            Apply for Free Coasters
            <ArrowRight className="w-6 h-6" />
          </button>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-white/50 text-sm font-body">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              100% free forever
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              No strings attached
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              5,000 coasters included
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForVenues;
